import { lbBrand } from '@fuse/colors';
import FuseUtils from '@fuse/utils';
import { addAccessTokenToUrl } from '@fuse/utils/url_utils';
import Icon from '@material-ui/core/Icon';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { RoleEnum } from 'app/auth/authRoles';
import { userRoleSelector, userUberallSelector } from 'app/auth/store/user.selector';
import { IconSource } from 'app/main/apps/categories/ducks/types';
import IconLB from 'app/main/design-system/components/icons/Icon';
import { navbarCloseMobile } from 'app/store/fuse/navbarSlice';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import FuseNavBadge from '../FuseNavBadge';

const useStyles = makeStyles(() => ({
  item: (props: { itemPadding: number }) => ({
    height: 40,
    width: 'calc(100% - 16px)',
    borderRadius: '0 20px 20px 0',
    paddingRight: 12,
    paddingLeft: props.itemPadding > 80 ? 80 : props.itemPadding,
    '&.active': {
      backgroundColor: lbBrand.red,
      color: `${lbBrand.white}!important`,
      pointerEvents: 'none',
      transition: 'border-radius .15s cubic-bezier(0.4,0.0,0.2,1)',
      '& .list-item-text-primary': {
        color: 'inherit',
      },
      '& .list-item-icon': {
        color: lbBrand.white,
      },
      '& .list-item-icon div': {
        color: lbBrand.white,
      },
    },
    '&:hover': {
      color: lbBrand.navHover,
      '& .list-item-icon': {
        color: lbBrand.navHover,
      },
      '& .list-item-icon div': {
        color: lbBrand.navHover,
      },
    },
    '& .list-item-icon': {
      marginRight: 16,
      color: lbBrand.navInactive,
    },
    '& .list-item-icon div': {
      color: lbBrand.navInactive,
    },
    '& .list-item-text': {},
    color: lbBrand.navInactive,
    textDecoration: 'none!important',
  }),
}));

interface FuseNavVerticalLinkProps {
  item: {
    id: string;
    title: string;
    icon: { name: string; source: IconSource };
    url: string;
    target: string;
    auth: RoleEnum[];
    translate: string;
    badge: {
      title: string;
      bg: string;
      fg: string;
    };
  };
  nestedLevel: number;
}

function FuseNavVerticalLink(props: RouteComponentProps & FuseNavVerticalLinkProps) {
  const dispatch = useDispatch();
  const userRole = useSelector(userRoleSelector);
  const uberall = useSelector(userUberallSelector);
  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down('md'));
  const { item, nestedLevel } = props;
  const classes = useStyles({
    itemPadding: nestedLevel > 0 ? 40 + nestedLevel * 16 : 24,
  });
  const { t } = useTranslation();

  const hasPermission = useMemo(() => FuseUtils.hasPermission(item.auth, userRole), [item.auth, userRole]);

  if (!hasPermission) {
    return null;
  }

  let { url } = item;

  if (item.id === 'uberall-integration') {
    url = uberall?.token ? addAccessTokenToUrl(url, { access_token: uberall?.token }) : url;
  }

  return (
    <ListItem
      button
      component="a"
      href={url}
      target={item.target ? item.target : '_blank'}
      rel={!item.target || item.target === '_blank' ? 'noreferrer noopener' : undefined}
      className={clsx(classes.item, 'list-item')}
      onClick={e => {
        e.currentTarget.blur();
        if (mdDown) {
          dispatch(navbarCloseMobile());
        }
      }}
      role="button"
    >
      {item.icon &&
        (item.icon.source === 'MaterialIcons' ? (
          <Icon className="list-item-icon text-16 flex-shrink-0">{item.icon.name}</Icon>
        ) : (
          <div className="list-item-icon text-16 flex-shrink-0">
            <IconLB source={item.icon.source} name={item.icon.name} size={18} />
          </div>
        ))}

      <ListItemText
        className="list-item-text"
        primary={item.translate ? t(`navigation.${item.translate}`) : item.title}
        classes={{ primary: 'text-14 list-item-text-primary' }}
      />

      {item.badge && <FuseNavBadge badge={item.badge} />}
    </ListItem>
  );
}

const NavVerticalLink = withRouter(React.memo(FuseNavVerticalLink));

export default NavVerticalLink;
